import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import "./AverageCogsPage.css";

const SubwaySpotCheckPage = () => {
  const { id: businessId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  
  const [expandedDate, setExpandedDate] = useState(null);
  const [storeNumber, setStoreNumber] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [data, setData] = useState({
    weighted_average_cogs_percentage: 0,
    row_count: 0,
    rows: [],
    totals: {
      net_sales: 0,
      cogs_amount: 0,
      payroll_amount: 0,
      payroll_tax_amount: 0,
      fixed_cost_total: 0,
      profit_loss_total: 0,
    },
  });

  const formatMoney = (value) => {
    const num = parseFloat(value || 0);
    const sign = num < 0 ? "-" : "";
    return `${sign}${Math.abs(num).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    })}`;
  };

  const formatDate = (value) => {
    if (!value) return "";
    const d = new Date(`${value}T00:00:00`);
    return d.toLocaleDateString();
  };

  useEffect(() => {
    const today = new Date();
    const prior = new Date();
    prior.setDate(today.getDate() - 6);

    const fmt = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setStartDate(fmt(prior));
    setEndDate(fmt(today));
  }, []);

  const fetchStoreNumber = useCallback(async () => {
    try {
      const response = await api.get(`/subway-data/business-store/${businessId}`);
      const resolvedStoreNumber = String(response.data?.store_number || "").trim();
      setStoreNumber(resolvedStoreNumber);
      return resolvedStoreNumber;
    } catch (err) {
      console.error("Failed to fetch business store number:", err);
      setStoreNumber("");
      return "";
    }
  }, [businessId]);

  const fetchSpotCheckData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/reports/subway-spotcheck/${businessId}`);
      setData({
        weighted_average_cogs_percentage: Number(
          res.data?.weighted_average_cogs_percentage || 0
        ),
        row_count: Number(res.data?.row_count || 0),
        rows: Array.isArray(res.data?.rows) ? res.data.rows : [],
        totals: res.data?.totals || {
          net_sales: 0,
          cogs_amount: 0,
          payroll_amount: 0,
          payroll_tax_amount: 0,
          fixed_cost_total: 0,
          profit_loss_total: 0,
        },
      });
    } catch (err) {
      console.error("Failed to fetch Subway spot check data:", err);
      alert(err?.response?.data?.message || "Could not load Subway spot check data.");
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    const load = async () => {
      await fetchStoreNumber();
      await fetchSpotCheckData();
    };

    load();
  }, [fetchStoreNumber, fetchSpotCheckData]);

  

  const filteredRows = useMemo(() => {
    if (!startDate && !endDate) return data.rows || [];

    return (data.rows || []).filter((row) => {
      const rowDate = String(row.date || "");
      if (startDate && rowDate < startDate) return false;
      if (endDate && rowDate > endDate) return false;
      return true;
    });
  }, [data.rows, startDate, endDate]);

  const filteredTotals = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => {
        acc.net_sales += Number(row.net_sales || 0);
        acc.cogs_amount += Number(row.cogs_amount || 0);
        acc.payroll_amount += Number(row.payroll_amount || 0);
        acc.payroll_tax_amount += Number(row.payroll_tax_amount || 0);
        acc.fixed_cost_total += Number(row.fixed_cost_total || 0);
        acc.profit_loss_total += Number(row.profit_loss_total || 0);
        return acc;
      },
      {
        net_sales: 0,
        cogs_amount: 0,
        payroll_amount: 0,
        payroll_tax_amount: 0,
        fixed_cost_total: 0,
        profit_loss_total: 0,
      }
    );
  }, [filteredRows]);

  return (
    <div className="payroll-sync-content">
    <div className="avg-cogs-container">
      <h1 className="pw-page-title">Subway Spot Check</h1>

      <div className="avg-cogs-topbar">
        <button
          type="button"
          className="avg-cogs-btn"
          onClick={() => navigate(`/dashboard/business/${businessId}/subway-data`)}
        >
          Subway Data
        </button>

        <button
          type="button"
          className="avg-cogs-btn"
          onClick={() => navigate(`/dashboard/business/${businessId}/fixed-costs`)}
        >
          Fixed Costs
        </button>

        

        
      </div>

      <div className="avg-cogs-pill">
        <div className="avg-cogs-account">
          Store Number: {storeNumber || "—"}
        </div>
        <div className="avg-cogs-note">
          Subway net sales drive daily food cost, Subway payroll hours drive labor.
        </div>
      </div>

      <div className="avg-cogs-input-card">
        

        <div className="avg-cogs-input-grid">
          <div className="avg-cogs-field">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="avg-cogs-field">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="avg-cogs-field readonly">
            <label>Weighted Average COGS %</label>
            <input
              type="text"
              value={`${Number(data.weighted_average_cogs_percentage || 0).toFixed(2)}%`}
              readOnly
            />
          </div>

          
        </div>
      </div>

      {loading ? (
        <div className="avg-cogs-loading">Loading...</div>
      ) : (
        <>
          <div className="avg-cogs-summary-grid">
            <div className="avg-cogs-card">
              <div className="avg-cogs-card-label">Total Net Sales</div>
              <div className="avg-cogs-card-value">
                {formatMoney(filteredTotals.net_sales)}
              </div>
            </div>

            <div className="avg-cogs-card">
              <div className="avg-cogs-card-label">Total Food Cost</div>
              <div className="avg-cogs-card-value">
                {formatMoney(filteredTotals.cogs_amount)}
              </div>
            </div>

            <div className="avg-cogs-card">
              <div className="avg-cogs-card-label">Total Payroll</div>
              <div className="avg-cogs-card-value">
                {formatMoney(filteredTotals.payroll_amount)}
              </div>
            </div>

            <div className="avg-cogs-card">
              <div className="avg-cogs-card-label">Total Fixed Costs</div>
              <div className="avg-cogs-card-value">
                {formatMoney(filteredTotals.fixed_cost_total)}
              </div>
            </div>

            <div className="avg-cogs-card avg-cogs-highlight">
              <div className="avg-cogs-card-label">Total Profit / Loss</div>
              <div className="avg-cogs-card-value">
                {formatMoney(filteredTotals.profit_loss_total)}
              </div>
            </div>
          </div>

          <div className="avg-cogs-table-wrap">
            <table className="avg-cogs-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Net Sales</th>
                  <th>Food Cost %</th>
                  <th>Food Cost</th>
                  <th>Payroll</th>
                  <th>Payroll Tax</th>
                  <th>Fixed Costs</th>
                  <th>Profit / Loss</th>
                  <th>Unmapped</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length > 0 ? (
                  filteredRows.map((row) => (
                    <React.Fragment key={row.date}>
                      <tr>
                        <td>{formatDate(row.date)}</td>
                        <td>{formatMoney(row.net_sales)}</td>
                        <td>{Number(row.cogs_percentage || 0).toFixed(2)}%</td>
                        <td>{formatMoney(row.cogs_amount)}</td>
                        <td>{formatMoney(row.payroll_amount)}</td>
                        <td>{formatMoney(row.payroll_tax_amount)}</td>
                        <td>{formatMoney(row.fixed_cost_total)}</td>
                        <td>{formatMoney(row.profit_loss_total)}</td>
                        <td>{row.unmapped_count || 0}</td>
                        <td>
                          <button
                            type="button"
                            className="avg-cogs-btn"
                            onClick={() =>
                              setExpandedDate((prev) =>
                                prev === row.date ? null : row.date
                              )
                            }
                          >
                            {expandedDate === row.date ? "Hide" : "Show"}
                          </button>
                        </td>
                      </tr>

                      {expandedDate === row.date && (
                        <tr>
                          <td colSpan="10" style={{ background: "#fff" }}>
                            <div style={{ padding: "16px" }}>
                              <h3 className="pw-page-title">Payroll Details</h3>

                              <div className="avg-cogs-payroll-table-wrap">
                                <table className="avg-cogs-payroll-table">
                                  <thead>
                                    <tr>
                                      <th>Subway Employee</th>
                                      <th>Mapped Payweek Employee</th>
                                      <th>Pay Type / Rate</th>
                                      <th>Hours</th>
                                      <th>Payroll Amount</th>
                                      <th>Mapped</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {row.payroll_rows?.length > 0 ? (
                                      row.payroll_rows.map((payRow, index) => (
                                        <tr key={`${row.date}-${index}`}>
                                          <td>{payRow.employee_name}</td>
                                          <td>{payRow.payweek_employee_name || "—"}</td>
                                          <td>{payRow.rate_label || "—"}</td>
                                          <td>{Number(payRow.hours_worked || 0).toFixed(2)}</td>
                                          <td>{formatMoney(payRow.payroll_amount)}</td>
                                          <td>{payRow.mapped ? "Yes" : "No"}</td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="6" className="avg-cogs-empty">
                                          No payroll rows found for this date.
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>

                              <h3 className="pw-page-title" style={{ marginTop: "20px" }}>
                                Fixed Cost Details
                              </h3>

                              <div className="avg-cogs-fixed-table-wrap">
                                <table className="avg-cogs-fixed-table">
                                  <thead>
                                    <tr>
                                      <th>Cost Name</th>
                                      <th>Type</th>
                                      <th>Monthly Amount</th>
                                      <th>Daily Amount</th>
                                      <th>Percentage</th>
                                      <th>Calculated Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {[
                                      ...(row.fixed_cost_rows?.amount_rows || []).map((cost) => ({
                                        ...cost,
                                        display_type: "Amount Per Month",
                                      })),
                                      ...(row.fixed_cost_rows?.percentage_rows || []).map((cost) => ({
                                        ...cost,
                                        display_type: "Percentage Of Sales",
                                      })),
                                    ].length > 0 ? (
                                      [
                                        ...(row.fixed_cost_rows?.amount_rows || []).map((cost) => ({
                                          ...cost,
                                          display_type: "Amount Per Month",
                                        })),
                                        ...(row.fixed_cost_rows?.percentage_rows || []).map((cost) => ({
                                          ...cost,
                                          display_type: "Percentage Of Sales",
                                        })),
                                      ].map((cost, index) => (
                                        <tr key={`${row.date}-cost-${index}`}>
                                          <td>{cost.cost_name}</td>
                                          <td>{cost.display_type}</td>
                                          <td>
                                            {cost.monthly_amount != null
                                              ? formatMoney(cost.monthly_amount)
                                              : "—"}
                                          </td>
                                          <td>
                                            {cost.daily_amount != null
                                              ? formatMoney(cost.daily_amount)
                                              : "—"}
                                          </td>
                                          <td>
                                            {cost.percentage != null
                                              ? `${Number(cost.percentage).toFixed(2)}%`
                                              : "—"}
                                          </td>
                                          <td>
                                            {cost.calculated_amount != null
                                              ? formatMoney(cost.calculated_amount)
                                              : "—"}
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="6" className="avg-cogs-empty">
                                          No fixed costs found for this date.
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>

                              {row.unmapped_count > 0 && (
                                <>
                                  <h3 className="pw-page-title" style={{ marginTop: "20px" }}>
                                    Unmapped Employees
                                  </h3>

                                  <div className="avg-cogs-table-wrap">
                                    <table className="avg-cogs-table">
                                      <thead>
                                        <tr>
                                          <th>Subway Employee</th>
                                          <th>Hours Worked</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {row.unmapped_employees.map((emp, index) => (
                                          <tr key={`${row.date}-unmapped-${index}`}>
                                            <td>{emp.employee_name}</td>
                                            <td>{Number(emp.hours_worked || 0).toFixed(2)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="avg-cogs-empty">
                      No Subway spot check rows found for the selected range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
    </div>
  );
};

export default SubwaySpotCheckPage;